import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ComicCard from '@/components/base/ComicCard';
import ScrollToTop from '@/components/base/ScrollToTop';
import { categories } from '@/mocks/categories';
import { getComicsByCategory } from '@/mocks/comics';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const [sortBy, setSortBy] = useState<'popular' | 'rating' | 'newest'>('popular');

  const currentCategory = categories.find((c) => c.slug === slug);
  const categoryComics = getComicsByCategory(slug || '');

  const sortedComics = [...categoryComics].sort((a, b) => {
    if (sortBy === 'rating') return b.rating - a.rating;
    if (sortBy === 'newest') return new Date(b.updateDate).getTime() - new Date(a.updateDate).getTime();
    return parseFloat(b.views.replace(/[亿万千]/g, (m) => m === '亿' ? '00000000' : m === '万' ? '0000' : '')) - parseFloat(a.views.replace(/[亿万千]/g, (m) => m === '亿' ? '00000000' : m === '万' ? '0000' : ''));
  });

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [slug]);

  if (!currentCategory) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="pt-20 pb-20 text-center">
          <h1 className="text-2xl font-heading text-foreground-950 mb-4">分类未找到</h1>
          <Link to="/" className="text-primary-500 hover:text-primary-600 cursor-pointer">返回首页</Link>
        </div>
        <Footer />
      </div>
    );
  }

  const pageSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${currentCategory.name}漫画 - 51漫画免费下载`,
    description: `51漫画${currentCategory.name}分类，${currentCategory.description}`,
    url: `https://51manhua.com/category/${slug}`,
    isPartOf: {
      '@type': 'WebSite',
      name: '51漫画',
      url: 'https://51manhua.com',
    },
  };

  return (
    <div className="min-h-screen bg-background-50">
      <ScrollToTop />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(pageSchema) }}
      />
      <Navbar />

      <main className="pt-20">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', to: '/' },
              { label: '分类浏览', to: '/category/热血格斗' },
              { label: currentCategory.name },
            ]}
          />

          <div className="relative rounded-lg overflow-hidden mb-8 h-48 md:h-56">
            <img
              src={currentCategory.image}
              alt={`51漫画 - ${currentCategory.name}分类`}
              title={`51漫画免费下载 - ${currentCategory.name}`}
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/60 to-transparent flex items-center">
              <div className="px-6 md:px-10">
                <h1 className="text-2xl md:text-3xl font-heading text-background-50 mb-2">
                  <strong>{currentCategory.name}</strong> 漫画
                </h1>
                <p className="text-sm text-background-50/80 max-w-md">{currentCategory.description}</p>
                <p className="text-xs text-background-50/60 mt-2">
                  共 <strong>{currentCategory.count}</strong> 部作品 · 51漫画免费下载
                </p>
              </div>
            </div>
          </div>

          {/* Sort Controls */}
          <div className="flex items-center gap-3 mb-6">
            <span className="text-xs text-foreground-500 whitespace-nowrap">排序：</span>
            <div className="flex items-center bg-background-100 rounded-full px-1 py-1">
              {([
                { key: 'popular', label: '最受欢迎' },
                { key: 'rating', label: '评分最高' },
                { key: 'newest', label: '最新更新' },
              ] as const).map((opt) => (
                <button
                  key={opt.key}
                  onClick={() => setSortBy(opt.key)}
                  className={`px-4 py-1.5 rounded-full text-xs font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                    sortBy === opt.key
                      ? 'bg-primary-500 text-background-50'
                      : 'text-foreground-600 hover:text-foreground-900'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {sortedComics.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5 pb-14" data-product-shop="">
              {sortedComics.map((comic) => (
                <ComicCard key={comic.id} comic={comic} />
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <p className="text-foreground-500">该分类暂无漫画</p>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}